import { useState, useCallback } from 'react';

const CAPTURE_WIDTH = 640;
const CAPTURE_HEIGHT = 480;

// MP4 우선, WebM 폴백
const getPreferredMimeType = () => {
    if (typeof MediaRecorder === 'undefined') return null;
    if (MediaRecorder.isTypeSupported('video/mp4')) return 'video/mp4';
    if (MediaRecorder.isTypeSupported('video/webm;codecs=h264')) return 'video/webm;codecs=h264';
    if (MediaRecorder.isTypeSupported('video/webm')) return 'video/webm';
    return null;
};

/**
 * 타임랩스 생성 전용 훅 (녹화는 StudyRoomContext에서 관리)
 * - 프레임 배열을 받아 MediaRecorder로 비디오 생성
 * - 오버레이, 페이드아웃, 요약 카드 렌더링
 */
const useTimelapseGenerator = () => {
    const [isGenerating, setIsGenerating] = useState(false);
    const [videoBlob, setVideoBlob] = useState(null);
    const [videoMimeType, setVideoMimeType] = useState(null);
    const [generateProgress, setGenerateProgress] = useState(0);
    const [generateError, setGenerateError] = useState(null);

    const generateTimelapse = useCallback(async (fps = 10, summaryData = null, frames = []) => {
        if (frames.length === 0) return null;

        setIsGenerating(true);
        setGenerateProgress(0);
        setGenerateError(null);

        try {
            const mimeType = getPreferredMimeType();
            if (!mimeType) {
                throw new Error('MediaRecorder not supported');
            }

            setVideoMimeType(mimeType);

            const canvas = document.createElement('canvas');
            canvas.width = CAPTURE_WIDTH;
            canvas.height = CAPTURE_HEIGHT;
            const ctx = canvas.getContext('2d');

            const stream = canvas.captureStream(fps);
            let recorder;
            try {
                recorder = new MediaRecorder(stream, {
                    mimeType,
                    videoBitsPerSecond: 2000000
                });
            } catch (recErr) {
                // mimeType 지원 실패 시 기본값으로 폴백
                console.warn('MediaRecorder mimeType fallback:', recErr);
                recorder = new MediaRecorder(stream);
            }

            const chunks = [];
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.push(e.data);
            };

            const blobType = mimeType.split(';')[0];
            const blobPromise = new Promise((resolve) => {
                recorder.onstop = () => {
                    resolve(new Blob(chunks, { type: blobType }));
                };
            });

            // 과목별 시간 집계 (프레임 timerInfo 기반)
            const subjectTimeMap = {};
            for (const frame of frames) {
                if (frame.timerInfo && frame.timerInfo.subjectName) {
                    const key = frame.timerInfo.subjectName;
                    if (!subjectTimeMap[key]) {
                        subjectTimeMap[key] = {
                            name: key,
                            color: frame.timerInfo.subjectColor || '#ccc',
                            icon: frame.timerInfo.subjectIcon || '📖',
                            maxSeconds: 0
                        };
                    }
                    if (frame.timerInfo.displaySeconds > subjectTimeMap[key].maxSeconds) {
                        subjectTimeMap[key].maxSeconds = frame.timerInfo.displaySeconds;
                    }
                }
            }

            // 프레임에 timerInfo가 없으면 서버의 studySessions 데이터로 보완
            if (Object.keys(subjectTimeMap).length === 0 && summaryData?.studySessions) {
                for (const ss of summaryData.studySessions) {
                    if (ss.subject_name && ss.total_seconds > 0) {
                        subjectTimeMap[ss.subject_name] = {
                            name: ss.subject_name,
                            color: ss.subject_color || '#ccc',
                            icon: '📖',
                            maxSeconds: ss.total_seconds
                        };
                    }
                }
            }

            const totalFrames = frames.length;
            const HOLD_FRAMES = 7;
            const FADE_FRAMES = 5;
            const SUMMARY_FRAMES = 30;
            const extraFrames = HOLD_FRAMES + FADE_FRAMES + SUMMARY_FRAMES;
            const frameDuration = 1000 / fps;

            // 첫 프레임 그리고 recorder 시작
            await drawFrame(ctx, frames[0], CAPTURE_WIDTH, CAPTURE_HEIGHT);
            recorder.start();
            await delay(frameDuration);
            setGenerateProgress(Math.round((1 / (totalFrames + extraFrames)) * 100));

            // 메인 프레임
            for (let i = 1; i < totalFrames; i++) {
                await drawFrame(ctx, frames[i], CAPTURE_WIDTH, CAPTURE_HEIGHT);
                setGenerateProgress(Math.round(((i + 1) / (totalFrames + extraFrames)) * 100));
                await delay(frameDuration);
            }

            // 마지막 장면 유지
            const lastFrame = frames[frames.length - 1];
            for (let i = 0; i < HOLD_FRAMES; i++) {
                await drawFrame(ctx, lastFrame, CAPTURE_WIDTH, CAPTURE_HEIGHT);
                setGenerateProgress(Math.round(((totalFrames + i + 1) / (totalFrames + extraFrames)) * 100));
                await delay(frameDuration);
            }

            // 페이드아웃
            for (let i = 0; i < FADE_FRAMES; i++) {
                await drawFrame(ctx, lastFrame, CAPTURE_WIDTH, CAPTURE_HEIGHT);
                ctx.fillStyle = `rgba(0,0,0,${(i + 1) / FADE_FRAMES})`;
                ctx.fillRect(0, 0, CAPTURE_WIDTH, CAPTURE_HEIGHT);
                setGenerateProgress(Math.round(((totalFrames + HOLD_FRAMES + i + 1) / (totalFrames + extraFrames)) * 100));
                await delay(frameDuration);
            }

            // 요약 카드
            const subjects = Object.values(subjectTimeMap);
            for (let i = 0; i < SUMMARY_FRAMES; i++) {
                drawSummaryCard(ctx, CAPTURE_WIDTH, CAPTURE_HEIGHT, subjects, summaryData);
                setGenerateProgress(Math.round(((totalFrames + HOLD_FRAMES + FADE_FRAMES + i + 1) / (totalFrames + extraFrames)) * 100));
                await delay(frameDuration);
            }

            await delay(frameDuration);
            recorder.stop();
            const blob = await blobPromise;
            setVideoBlob(blob);
            setGenerateProgress(100);
            return blob;
        } catch (error) {
            console.error('Timelapse generation failed:', error);
            setGenerateError(error.message || 'Generation failed');
            return null;
        } finally {
            setIsGenerating(false);
        }
    }, []);

    const resetVideo = useCallback(() => {
        setVideoBlob(null);
        setGenerateProgress(0);
        setGenerateError(null);
    }, []);

    return {
        isGenerating,
        videoBlob,
        videoMimeType,
        generateProgress,
        generateError,
        generateTimelapse,
        resetVideo
    };
};

// 유틸: 프레임 그리기 (오버레이 포함)
async function drawFrame(ctx, frame, width, height) {
    if (!frame.blob || !(frame.blob instanceof Blob)) {
        // IDB에서 가져온 데이터가 Blob이 아닌 경우 스킵
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, width, height);
        return;
    }
    const img = await createImageBitmap(frame.blob);
    ctx.drawImage(img, 0, 0, width, height);
    img.close();

    if (frame.timerInfo) {
        drawTimerOverlay(ctx, width, height, frame.timerInfo);
    }
}

function drawTimerOverlay(ctx, width, height, timerInfo) {
    const barHeight = 48;
    const y = height - barHeight;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(0, y + 10);
    ctx.quadraticCurveTo(0, y, 10, y);
    ctx.lineTo(width - 10, y);
    ctx.quadraticCurveTo(width, y, width, y + 10);
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.closePath();
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.fill();
    ctx.restore();

    const icon = timerInfo.subjectIcon || '📖';
    const subjectName = timerInfo.subjectName || '';

    ctx.save();
    ctx.textBaseline = 'middle';
    const centerY = y + barHeight / 2;

    ctx.font = '22px sans-serif';
    ctx.fillText(icon, 12, centerY);

    ctx.font = '17px sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 3;
    ctx.fillText(subjectName, 40, centerY);
    ctx.restore();

    const secs = timerInfo.displaySeconds || 0;
    const h = String(Math.floor(secs / 3600)).padStart(2, '0');
    const m = String(Math.floor((secs % 3600) / 60)).padStart(2, '0');
    const s = String(secs % 60).padStart(2, '0');
    const timeStr = timerInfo.status === 'paused' ? `⏸ ${h}:${m}:${s}` : `${h}:${m}:${s}`;

    ctx.save();
    ctx.textBaseline = 'middle';
    ctx.font = '20px monospace';
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 3;
    ctx.textAlign = 'right';
    ctx.fillText(timeStr, width - 12, y + barHeight / 2);
    ctx.restore();
}

function drawSummaryCard(ctx, width, height, subjects, summaryData) {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);

    ctx.textBaseline = 'top';
    ctx.textAlign = 'center';

    const timeInfoY = 24;
    ctx.font = 'bold 22px sans-serif';
    ctx.fillStyle = '#ffffff';

    if (subjects.length > 0) {
        ctx.fillText('📊 Study Summary', width / 2, timeInfoY);
    } else {
        ctx.fillText('📚 Study Room', width / 2, timeInfoY);
    }

    let contentY = 58;
    if (summaryData && summaryData.roomOpenTime) {
        const openTime = new Date(summaryData.roomOpenTime);
        const closeTime = summaryData.roomCloseTime ? new Date(summaryData.roomCloseTime) : new Date();
        const openStr = `${openTime.getHours().toString().padStart(2, '0')}:${openTime.getMinutes().toString().padStart(2, '0')}`;
        const closeStr = `${closeTime.getHours().toString().padStart(2, '0')}:${closeTime.getMinutes().toString().padStart(2, '0')}`;
        ctx.font = '16px sans-serif';
        ctx.fillStyle = '#aaaaaa';
        ctx.fillText(`${openStr} ~ ${closeStr}`, width / 2, contentY);
        contentY += 28;

        if (summaryData.totalDuration) {
            ctx.font = 'bold 24px monospace';
            ctx.fillStyle = '#4CAF50';
            ctx.fillText(formatDuration(summaryData.totalDuration), width / 2, contentY);
            contentY += 36;
        }
    }

    if (subjects.length > 0) {
        ctx.strokeStyle = '#333333';
        ctx.beginPath();
        ctx.moveTo(30, contentY);
        ctx.lineTo(width - 30, contentY);
        ctx.stroke();
        contentY += 16;

        const maxDisplay = Math.min(subjects.length, 6);
        ctx.textAlign = 'left';
        for (let i = 0; i < maxDisplay; i++) {
            const subj = subjects[i];
            const yPos = contentY + i * 34;

            ctx.font = '15px sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.fillText(`${subj.icon} ${subj.name}`, 30, yPos);

            ctx.textAlign = 'right';
            ctx.font = '15px monospace';
            ctx.fillStyle = subj.color;
            ctx.fillText(formatDuration(subj.maxSeconds), width - 30, yPos);
            ctx.textAlign = 'left';
        }
    } else if (!summaryData) {
        ctx.font = 'bold 22px sans-serif';
        ctx.fillStyle = '#ffffff';
        ctx.fillText('📚 Study Complete!', width / 2, height / 2 - 10);
    }

    ctx.textAlign = 'center';
    ctx.font = '13px sans-serif';
    ctx.fillStyle = '#666666';
    ctx.fillText('studyhalltimer.com', width / 2, height - 28);
}

function formatDuration(seconds) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
        return `${h}h ${String(m).padStart(2, '0')}m`;
    }
    return `${m}m ${String(s).padStart(2, '0')}s`;
}

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export default useTimelapseGenerator;
